import {autoUpdate, flip, offset, shift, useFloating} from '@floating-ui/react';
import {BigCalendarUser} from 'platform/big-calendar';
import {Flag, Portal, PortalManager, Separator} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {useTheme} from 'styled-components';

import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER} from '@dms/shared';

import {isNotNilOrEmpty, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {OutlookCalendarEvent} from '../../types/OutlookCalendarEvent';
import {getOutlookEventDateSpan} from './utils/getOutlookEventDateSpan';
import {truncateOutlookEventDescription} from './utils/truncateOutlookEventDescription';

interface OutlookEventPopupProps extends RequiredTestIdProps {
  event: OutlookCalendarEvent;
  anchorElement: HTMLElement | null;
  isVisible: boolean;
  users: BigCalendarUser[] | Nullish;
}

const OFFSET_FROM_ANCHOR = 10;
const POPUP_PADDING = 8;
const POPUP_WIDTH = 90;

export function OutlookEventPopup(props: OutlookEventPopupProps) {
  const theme = useTheme();
  const formatDateTime = useDateTimeFormatter();

  const {refs, floatingStyles} = useFloating({
    placement: 'right-start',
    strategy: 'fixed',
    middleware: [
      offset(OFFSET_FROM_ANCHOR),
      flip({
        fallbackPlacements: ['right-end', 'left-start', 'left-end', 'top', 'bottom'],
        padding: POPUP_PADDING,
      }),
      shift({padding: POPUP_PADDING}),
    ],
    elements: {reference: props.anchorElement},
    whileElementsMounted: autoUpdate,
  });
  // Pulled out of `refs` up front: reading `refs.setFloating` inside JSX trips the react-compiler
  // lint as a ref access during render.
  const {setFloating} = refs;

  if (!props.isVisible) {
    return null;
  }

  const owner = props.users?.find((user) => user.id === props.event.userId);
  const description = truncateOutlookEventDescription(props.event.description);

  return (
    <PortalManager zIndex={theme.zIndices.TOOLTIP}>
      <Portal>
        <div ref={setFloating} css={{...floatingStyles, pointerEvents: 'none'}}>
          <Box
            backgroundColor="general.white"
            border="1px solid"
            borderColor="palettes.neutral.40.100"
            borderRadius="medium"
            padding={4}
            boxShadow="elevation_4"
            width={POPUP_WIDTH}
            maxWidth={POPUP_WIDTH}
          >
            <VStack spacing={3}>
              <HStack spacing={2} align="center" wrap="nowrap">
                <Heading size={4} data-testid={suffixTestId('title', props)}>
                  {props.event.title}
                </Heading>
                <Flag
                  size="small"
                  colorScheme="green"
                  label={i18n.t('entity.calendarEvent.labels.outlookEvent')}
                  data-testid={suffixTestId('origin', props)}
                />
              </HStack>

              <VStack spacing={2}>
                <HStack spacing={2} align="center">
                  <Icon value="action/calendar_today" size={4} color="text.secondary" />
                  <Text size="small" data-testid={suffixTestId('dateSpan', props)}>
                    {getOutlookEventDateSpan(props.event, formatDateTime)}
                  </Text>
                </HStack>

                <Show when={isNotNilOrEmpty(props.event.location)}>
                  <HStack spacing={2} align="center">
                    <Icon value="maps/place" size={4} color="text.secondary" />
                    <Text size="small" data-testid={suffixTestId('location', props)}>
                      {props.event.location}
                    </Text>
                  </HStack>
                </Show>

                <HStack spacing={2} align="center">
                  <Icon value="social/person" size={4} color="text.secondary" />
                  <Text size="small" data-testid={suffixTestId('owner', props)}>
                    {owner?.fullName ?? EMPTY_PLACEHOLDER}
                  </Text>
                </HStack>

                <Show when={isNotNilOrEmpty(description)}>
                  <HStack spacing={2} align="flex-start">
                    <Icon value="action/subject" size={4} color="text.secondary" />
                    <Text
                      size="small"
                      preWrap
                      overflowWrap="anywhere"
                      data-testid={suffixTestId('description', props)}
                    >
                      {description}
                    </Text>
                  </HStack>
                </Show>
              </VStack>

              <Separator spacing={0} />

              <Text size="xSmall" color="secondary" data-testid={suffixTestId('readOnly', props)}>
                {i18n.t('entity.calendarEvent.labels.readOnlyInDms')}
              </Text>
            </VStack>
          </Box>
        </div>
      </Portal>
    </PortalManager>
  );
}
